/**
 * 나의 대회 (personal tournaments) contracts — module `personal` (CLAUDE.md §4.10, §5 personal,
 * §15 #405–#416). Any signed-in USER gets a personal space (a PERSONAL tenant) where every existing
 * tournament route works; a personal tournament can open registration / start only after it is
 * activated with a credit (subscription period, coupon, one-time fee or operator grant). The
 * personal member list ("내 멤버") + invite link replace clubs.
 * Names are prefixed `Personal*` (user routes) and `PlatformPersonal*` (super admin routes).
 */
import { z } from 'zod';
import {
  Gender,
  PersonalGrantSource,
  PersonalMemberSource,
  PersonalOrderKind,
  PersonalOrderStatus,
  PlayProductKind,
  PlayPurchaseState,
  TournamentStatus,
  UserStatus,
} from '../enums';
import { LocaleSchema } from '../i18n';

export const PersonalErrorCode = {
  /** 402: open registration / start a round / go IN_PROGRESS on a personal tournament without activation */
  PERSONAL_ACTIVATION_REQUIRED: 'PERSONAL_ACTIVATION_REQUIRED',
  /** 402: no usable credit for this activation (details.players) */
  PERSONAL_NO_CREDIT: 'PERSONAL_NO_CREDIT',
  /** 409 */
  PERSONAL_ALREADY_ACTIVATED: 'PERSONAL_ALREADY_ACTIVATED',
  /** 409: more APPROVED players than the activation allows (details {maxPlayers, approved}) */
  PERSONAL_PLAYER_LIMIT: 'PERSONAL_PLAYER_LIMIT',
  /** 400: players outside min..max of the price / the credit's cap (details {min, max}) */
  PERSONAL_PLAYERS_OUT_OF_RANGE: 'PERSONAL_PLAYERS_OUT_OF_RANGE',
  /** 404 unknown or disabled code; 410 expired / used up */
  COUPON_INVALID: 'COUPON_INVALID',
  COUPON_EXPIRED: 'COUPON_EXPIRED',
  COUPON_EXHAUSTED: 'COUPON_EXHAUSTED',
  COUPON_CODE_TAKEN: 'COUPON_CODE_TAKEN',
  /** 409: approve/reject/cancel an order that is no longer PENDING */
  ORDER_NOT_PENDING: 'ORDER_NOT_PENDING',
  /** 409: the same request is already waiting for the operator (details.orderId) */
  ORDER_PENDING_EXISTS: 'ORDER_PENDING_EXISTS',
  /** 409: currency without an active one-time price / inactive plan */
  PRICE_NOT_AVAILABLE: 'PRICE_NOT_AVAILABLE',
  PLAN_NOT_AVAILABLE: 'PLAN_NOT_AVAILABLE',
  /** 409: bank reference already recorded for another order */
  PAYMENT_REF_TAKEN: 'PAYMENT_REF_TAKEN',
  /** 409 member list: same phone / profile already on the list */
  MEMBER_EXISTS: 'MEMBER_EXISTS',
  /** 410 invite states */
  INVITE_EXPIRED: 'INVITE_EXPIRED',
  INVITE_REVOKED: 'INVITE_REVOKED',
  INVITE_EXHAUSTED: 'INVITE_EXHAUSTED',
  /** 409: the inviter cannot join their own list */
  INVITE_OWN_LIST: 'INVITE_OWN_LIST',
  /** 503: Google Play purchases are not configured on this server (PLAY_BILLING_KEY_B64 unset) */
  PLAY_BILLING_DISABLED: 'PLAY_BILLING_DISABLED',
  /** 400: productId is not one of PLAY_BILLING_PRODUCTS */
  PLAY_PRODUCT_UNKNOWN: 'PLAY_PRODUCT_UNKNOWN',
  /** 400: Play does not know the token, or it belongs to another product / app */
  PLAY_PURCHASE_INVALID: 'PLAY_PURCHASE_INVALID',
  /** 409: the token was already granted to another PinGo account */
  PLAY_PURCHASE_OTHER_ACCOUNT: 'PLAY_PURCHASE_OTHER_ACCOUNT',
  /** 503: the Android Publisher API failed; the app retries (the purchase stays on the device) */
  PLAY_UNAVAILABLE: 'PLAY_UNAVAILABLE',
} as const;
export type PersonalErrorCode = (typeof PersonalErrorCode)[keyof typeof PersonalErrorCode];

/** Decimal money as a string ("32000.00"). */
const Money = z.string();
const MoneyInput = z.string().regex(/^\d{1,14}(\.\d{1,2})?$/);
const Currency = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .describe('ISO 4217');
const page = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

// =============================================================================================
// catalog (what can be bought)
// =============================================================================================

export const PersonalPlanSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  currency: z.string(),
  price: Money,
  periodDays: z.number(),
  tournamentQuota: z.number(),
  maxPlayers: z.number().nullable(),
  active: z.boolean(),
  sortOrder: z.number(),
});
export type PersonalPlan = z.infer<typeof PersonalPlanSchema>;

export const PersonalPriceSchema = z.object({
  currency: z.string(),
  unitPrice: Money,
  minPlayers: z.number(),
  maxPlayers: z.number(),
  active: z.boolean(),
});
export type PersonalPrice = z.infer<typeof PersonalPriceSchema>;

// =============================================================================================
// entitlements
// =============================================================================================

export const PersonalGrantSchema = z.object({
  id: z.uuid(),
  source: PersonalGrantSource,
  credits: z.number(),
  used: z.number(),
  remaining: z.number(),
  /** players per tournament (null = the price's maxPlayers) */
  maxPlayers: z.number().nullable(),
  validFrom: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  /** remaining > 0, started and not expired */
  usable: z.boolean(),
  planName: z.string().nullable(),
  couponCode: z.string().nullable(),
  /** ONE_TIME: the tournament it was paid for */
  tournamentId: z.uuid().nullable(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type PersonalGrant = z.infer<typeof PersonalGrantSchema>;

export const PersonalActivationSchema = z.object({
  tournamentId: z.uuid(),
  grantId: z.uuid(),
  source: PersonalGrantSource,
  maxPlayers: z.number(),
  activatedAt: z.iso.datetime(),
});
export type PersonalActivation = z.infer<typeof PersonalActivationSchema>;

export const PersonalOrderSchema = z.object({
  id: z.uuid(),
  /** 'manual' (bank transfer, operator approval) | 'google-play'; null while PENDING (additive) */
  paymentProvider: z.string().nullable().optional(),
  /** Google Play: product id of the store purchase (additive) */
  storeProductId: z.string().nullable().optional(),
  kind: PersonalOrderKind,
  status: PersonalOrderStatus,
  planId: z.uuid().nullable(),
  planName: z.string().nullable(),
  tournamentId: z.uuid().nullable(),
  tournamentName: z.string().nullable(),
  players: z.number().nullable(),
  /** ONE_TIME on an already activated tournament: extra players */
  topUp: z.boolean(),
  currency: z.string(),
  amount: Money,
  payerNote: z.string().nullable(),
  paymentRef: z.string().nullable(),
  paidAmount: Money.nullable(),
  decisionNote: z.string().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type PersonalOrder = z.infer<typeof PersonalOrderSchema>;

export const PersonalTournamentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: TournamentStatus,
  startDate: z.iso.date(),
  endDate: z.iso.date(),
  approvedPlayers: z.number(),
  activation: PersonalActivationSchema.nullable(),
  /** a PENDING one-time order for this tournament */
  pendingOrderId: z.uuid().nullable(),
});
export type PersonalTournament = z.infer<typeof PersonalTournamentSchema>;

// =============================================================================================
// store purchases (Google Play Billing, CLAUDE.md §15 #428–#437) + launch free period
// =============================================================================================

/** One sellable store product (PLAY_BILLING_PRODUCTS). Prices come from the store, not from us. */
export const PersonalStoreProductSchema = z.object({
  productId: z.string(),
  kind: PlayProductKind,
  /** SUBSCRIPTION: the Play base plan to buy (null = the first one Play returns) */
  basePlanId: z.string().nullable(),
  /** CREDIT: players per tournament; SUBSCRIPTION: players per activated tournament (null = no cap) */
  maxPlayers: z.number().nullable(),
  /** SUBSCRIPTION: activations per billing period */
  tournamentQuota: z.number().nullable(),
});
export type PersonalStoreProduct = z.infer<typeof PersonalStoreProductSchema>;

export const PersonalStoreSchema = z.object({
  google: z.object({
    /** false = no service-account key on the server: the app hides the buy buttons */
    enabled: z.boolean(),
    products: z.array(PersonalStoreProductSchema),
    /** pass as obfuscatedAccountId when buying (sha256 of the user id) */
    accountId: z.string().nullable(),
  }),
});
export type PersonalStore = z.infer<typeof PersonalStoreSchema>;

/** Launch free period: activation needs no credit until (and including) `until`. */
export const PersonalFreePeriodSchema = z.object({
  /** last free day (yyyy-MM-dd, platform timezone Asia/Ho_Chi_Minh) */
  until: z.iso.date(),
  /** most players a free activation may allow */
  maxPlayers: z.number(),
});
export type PersonalFreePeriod = z.infer<typeof PersonalFreePeriodSchema>;

export const PersonalPlayPurchaseSchema = z.object({
  id: z.uuid(),
  productId: z.string(),
  basePlanId: z.string().nullable(),
  kind: PlayProductKind,
  state: PlayPurchaseState,
  /** consumed (credits) / acknowledged (subscriptions) on Play */
  finished: z.boolean(),
  autoRenewing: z.boolean().nullable(),
  /** subscriptions: end of the current paid period */
  expiresAt: z.iso.datetime().nullable(),
  testPurchase: z.boolean(),
  latestOrderId: z.string().nullable(),
  voidedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type PersonalPlayPurchase = z.infer<typeof PersonalPlayPurchaseSchema>;

/** POST /personal/purchases/google — verify a Play purchase token and grant the entitlement */
export const PersonalGooglePurchaseRequestSchema = z.object({
  productId: z.string().trim().min(1).max(150),
  purchaseToken: z.string().trim().min(10).max(4096),
  /** CREDIT bought from a tournament screen: activate that tournament with it (owner only) */
  tournamentId: z.uuid().optional(),
  /** store price as the app saw it (display only — Play Console is the price authority) */
  priceAmountMicros: z
    .string()
    .regex(/^\d{1,18}$/)
    .optional(),
  currency: Currency.optional(),
});
export type PersonalGooglePurchaseRequest = z.infer<typeof PersonalGooglePurchaseRequestSchema>;

export const PersonalGooglePurchaseResponseSchema = z.object({
  /**
   * GRANTED: new credit(s); ALREADY_GRANTED: this token / order was processed before (idempotent);
   * PENDING: Play has not charged yet (no credit, send again later); NOT_ACTIVE: cancelled,
   * expired, revoked or refunded on Play
   */
  status: z.enum(['GRANTED', 'ALREADY_GRANTED', 'PENDING', 'NOT_ACTIVE']),
  purchase: PersonalPlayPurchaseSchema,
  grant: PersonalGrantSchema.nullable(),
  activation: PersonalActivationSchema.nullable(),
  /** consumed / acknowledged on Play by the server; false = the app should finish it itself */
  finished: z.boolean(),
});
export type PersonalGooglePurchaseResponse = z.infer<typeof PersonalGooglePurchaseResponseSchema>;

/** GET /me/personal — everything the "나의 대회" screen needs. */
export const PersonalOverviewSchema = z.object({
  /** null until the space is created (POST /me/personal/space) */
  tenantId: z.uuid().nullable(),
  tournaments: z.array(PersonalTournamentSchema),
  /** usable credits (sum of remaining over usable grants) */
  availableCredits: z.number(),
  grants: z.array(PersonalGrantSchema),
  /** current subscription period (latest usable SUBSCRIPTION grant) */
  subscription: z
    .object({ planName: z.string().nullable(), expiresAt: z.iso.datetime().nullable() })
    .nullable(),
  orders: z.array(PersonalOrderSchema),
  plans: z.array(PersonalPlanSchema),
  prices: z.array(PersonalPriceSchema),
  memberCount: z.number(),
  /** operator's transfer details (api env PERSONAL_PAYMENT_INSTRUCTIONS); null = not configured */
  paymentInstructions: z.string().nullable().optional(),
  /** clubs the user administered before clubs were merged (one-time import, §15 #413) */
  formerClubs: z.array(z.object({ clubId: z.uuid(), name: z.string(), activeMembers: z.number() })),
  /** launch free period while active (activation needs no credit) — additive */
  freePeriod: PersonalFreePeriodSchema.nullable().optional(),
  /** in-app store products (Google Play) — additive */
  store: PersonalStoreSchema.optional(),
});
export type PersonalOverview = z.infer<typeof PersonalOverviewSchema>;

/** POST /me/personal/space (idempotent) */
export const PersonalCreateSpaceRequestSchema = z.object({
  timezone: z.string().max(60).optional(),
  country: z.string().length(2).optional(),
  defaultLocale: LocaleSchema.optional(),
});
export const PersonalSpaceSchema = z.object({ tenantId: z.uuid(), created: z.boolean() });
export type PersonalSpace = z.infer<typeof PersonalSpaceSchema>;

/** POST /me/personal/coupons/redeem (10/min) — the same user redeeming again gets alreadyRedeemed */
export const PersonalRedeemRequestSchema = z.object({ code: z.string().trim().min(4).max(40) });
export const PersonalRedeemResponseSchema = z.object({
  grant: PersonalGrantSchema,
  alreadyRedeemed: z.boolean(),
});
export type PersonalRedeemResponse = z.infer<typeof PersonalRedeemResponseSchema>;

/** POST /me/personal/orders → 201 PersonalOrder (PENDING) */
export const PersonalCreateOrderRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('SUBSCRIPTION'),
    planId: z.uuid(),
    payerNote: z.string().trim().max(200).optional(),
  }),
  z.object({
    kind: z.literal('ONE_TIME'),
    tournamentId: z.uuid(),
    players: z.int().min(1).max(1000),
    currency: Currency,
    payerNote: z.string().trim().max(200).optional(),
  }),
]);
export type PersonalCreateOrderRequest = z.infer<typeof PersonalCreateOrderRequestSchema>;

/** POST /tournaments/:tournamentId/personal/activate */
export const PersonalActivateRequestSchema = z.object({
  players: z.int().min(1).max(1000),
  /** default: the usable grant expiring first that allows `players` */
  grantId: z.uuid().optional(),
});

/** GET /tournaments/:tournamentId/personal (T:VIEW, personal tournaments only — else 404) */
export const PersonalTournamentStateSchema = z.object({
  tournamentId: z.uuid(),
  activation: PersonalActivationSchema.nullable(),
  approvedPlayers: z.number(),
  availableCredits: z.number(),
  pendingOrderId: z.uuid().nullable(),
  prices: z.array(PersonalPriceSchema),
  /** launch free period while active — additive */
  freePeriod: PersonalFreePeriodSchema.nullable().optional(),
  /** the caller's own linked entry (PENDING/APPROVED/WAITLISTED), null = not playing — additive */
  myParticipantId: z.uuid().nullable().optional(),
});
export type PersonalTournamentState = z.infer<typeof PersonalTournamentStateSchema>;

// =============================================================================================
// member list ("내 멤버") + invites — replaces clubs (§15 #411–#414)
// =============================================================================================

export const PersonalMemberSchema = z.object({
  id: z.uuid(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  gender: Gender.nullable(),
  birthYear: z.number().nullable(),
  handicapIndex: z.number().nullable(),
  /** joined with their own account (or imported from a club): results and PinGo HI link up */
  linked: z.boolean(),
  source: PersonalMemberSource,
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type PersonalMember = z.infer<typeof PersonalMemberSchema>;

export const PersonalMemberInputSchema = z.object({
  fullName: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(6).max(32).nullable().optional(),
  email: z.email().max(200).nullable().optional(),
  gender: Gender.nullable().optional(),
  birthYear: z.int().min(1900).max(2100).nullable().optional(),
  handicapIndex: z.number().min(-10).max(54).nullable().optional(),
  note: z.string().trim().max(200).nullable().optional(),
});
export type PersonalMemberInput = z.infer<typeof PersonalMemberInputSchema>;
export const PersonalMemberListSchema = z.object({ items: z.array(PersonalMemberSchema) });

/** POST /me/personal/members/import-club {clubId} — former CLUB_ADMIN copies ACTIVE members once */
export const PersonalImportClubRequestSchema = z.object({ clubId: z.uuid() });
export const PersonalImportResultSchema = z.object({
  added: z.number(),
  skipped: z.number(),
});

/** POST /tournaments/:tournamentId/personal/members → add list members as APPROVED participants */
export const PersonalAddMembersRequestSchema = z.object({
  memberIds: z.array(z.uuid()).min(1).max(300),
});
export const PersonalAddMembersResultSchema = z.object({
  created: z.number(),
  /** already in the tournament (same linked profile or phone) */
  skipped: z.number(),
  participantIds: z.array(z.uuid()),
});

/** POST /tournaments/:tid/personal/join — the caller plays in their own tournament (§15 #451) */
export const PersonalJoinResultSchema = z.object({
  participantId: z.uuid(),
  /** false = the caller already had an entry (idempotent) */
  created: z.boolean(),
});
export type PersonalJoinResult = z.infer<typeof PersonalJoinResultSchema>;

/**
 * POST /tournaments/:tid/personal/leave (§15 #482): the caller stops playing — the entry is deleted
 * when it has no records, else WITHDRAWN (scores and audits stay). left false = no entry.
 */
export const PersonalLeaveResultSchema = z.object({
  left: z.boolean(),
  /** true = removed; false with left = WITHDRAWN (it has scores) */
  deleted: z.boolean(),
});
export type PersonalLeaveResult = z.infer<typeof PersonalLeaveResultSchema>;

export const PersonalInviteSchema = z.object({
  id: z.uuid(),
  token: z.string(),
  url: z.string(),
  maxUses: z.number().nullable(),
  uses: z.number(),
  expiresAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  state: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED', 'EXHAUSTED']),
  createdAt: z.iso.datetime(),
});
export type PersonalInvite = z.infer<typeof PersonalInviteSchema>;
export const PersonalCreateInviteRequestSchema = z.object({
  maxUses: z.int().min(1).max(1000).nullable().optional(),
  expiresAt: z.iso.datetime().nullable().optional(),
});
export const PersonalInviteListSchema = z.object({ items: z.array(PersonalInviteSchema) });

/** GET /public/personal-invites/:token (PUB) */
export const PersonalInvitePreviewSchema = z.object({
  ownerName: z.string(),
  state: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED', 'EXHAUSTED']),
  memberCount: z.number(),
});
export type PersonalInvitePreview = z.infer<typeof PersonalInvitePreviewSchema>;
/** POST /personal-invites/:token/accept (USER) */
export const PersonalAcceptInviteResponseSchema = z.object({
  ownerName: z.string(),
  alreadyMember: z.boolean(),
});

// =============================================================================================
// platform (super admin)
// =============================================================================================

export const PlatformPersonalCouponSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  credits: z.number(),
  maxPlayers: z.number().nullable(),
  validDays: z.number().nullable(),
  maxRedemptions: z.number().nullable(),
  redemptionCount: z.number(),
  expiresAt: z.iso.datetime().nullable(),
  disabledAt: z.iso.datetime().nullable(),
  state: z.enum(['ACTIVE', 'EXPIRED', 'EXHAUSTED', 'DISABLED']),
  createdAt: z.iso.datetime(),
});
export type PlatformPersonalCoupon = z.infer<typeof PlatformPersonalCouponSchema>;
export const PlatformPersonalCouponListSchema = page(PlatformPersonalCouponSchema);

export const PlatformPersonalCreateCouponRequestSchema = z.object({
  /** omitted = generated (10 characters, no 0/O/1/I) */
  code: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9-]{4,40}$/)
    .optional(),
  name: z.string().trim().min(1).max(120),
  credits: z.int().min(1).max(100).default(1),
  maxPlayers: z.int().min(1).max(1000).nullable().optional(),
  validDays: z.int().min(1).max(3650).nullable().optional(),
  maxRedemptions: z.int().min(1).max(100000).nullable().optional(),
  expiresAt: z.iso.datetime().nullable().optional(),
});
export type PlatformPersonalCreateCouponRequest = z.input<
  typeof PlatformPersonalCreateCouponRequestSchema
>;
export const PlatformPersonalUpdateCouponRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  disabled: z.boolean().optional(),
  expiresAt: z.iso.datetime().nullable().optional(),
  maxRedemptions: z.int().min(1).max(100000).nullable().optional(),
});

export const PlatformPersonalPlanRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  currency: Currency.default('KRW'),
  price: MoneyInput,
  periodDays: z.int().min(1).max(3660).default(30),
  tournamentQuota: z.int().min(1).max(1000),
  maxPlayers: z.int().min(1).max(1000).nullable().optional(),
  active: z.boolean().default(true),
  sortOrder: z.int().min(0).max(1000).default(0),
});
export type PlatformPersonalPlanRequest = z.input<typeof PlatformPersonalPlanRequestSchema>;
export const PlatformPersonalPlanUpdateSchema = PlatformPersonalPlanRequestSchema.partial();
export const PlatformPersonalPlanListSchema = z.object({ items: z.array(PersonalPlanSchema) });

export const PlatformPersonalPriceRequestSchema = z.object({
  unitPrice: MoneyInput,
  minPlayers: z.int().min(1).max(1000),
  maxPlayers: z.int().min(1).max(1000),
  active: z.boolean(),
});
export const PlatformPersonalPriceListSchema = z.object({ items: z.array(PersonalPriceSchema) });

export const PlatformPersonalUserRefSchema = z.object({
  id: z.uuid(),
  email: z.string().nullable(),
  displayName: z.string(),
});
export const PlatformPersonalOrderSchema = PersonalOrderSchema.extend({
  user: PlatformPersonalUserRefSchema,
});
export type PlatformPersonalOrder = z.infer<typeof PlatformPersonalOrderSchema>;
export const PlatformPersonalOrderListSchema = page(PlatformPersonalOrderSchema);
export const PlatformPersonalOrdersQuerySchema = z.object({
  status: PersonalOrderStatus.optional(),
  userId: z.uuid().optional(),
  /** 'manual' | 'google-play' */
  provider: z
    .string()
    .regex(/^[a-z0-9-]{1,40}$/)
    .optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** POST /platform/personal/orders/:orderId/approve — records a manual payment, grants credits */
export const PlatformPersonalApproveRequestSchema = z.object({
  /** bank transfer reference (unique per provider) */
  providerRef: z.string().trim().min(1).max(120),
  /** default = order amount */
  amount: MoneyInput.optional(),
  note: z.string().trim().max(500).optional(),
});
export const PlatformPersonalRejectRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

/** POST /platform/personal/users/:userId/grants — comp / refund credits (reason audited) */
export const PlatformPersonalGrantRequestSchema = z.object({
  credits: z.int().min(1).max(1000),
  maxPlayers: z.int().min(1).max(1000).nullable().optional(),
  validDays: z.int().min(1).max(3650).nullable().optional(),
  note: z.string().trim().min(1).max(500),
});

export const PlatformPersonalUserSchema = z.object({
  user: PlatformPersonalUserRefSchema.extend({ status: UserStatus }),
  tenantId: z.uuid().nullable(),
  availableCredits: z.number(),
  grants: z.array(PersonalGrantSchema),
  orders: z.array(PersonalOrderSchema),
  tournaments: z.array(PersonalTournamentSchema),
});
export type PlatformPersonalUser = z.infer<typeof PlatformPersonalUserSchema>;

/** GET /platform/personal/play-purchases — Google Play purchase tokens with their orders */
export const PlatformPersonalPlayPurchaseSchema = PersonalPlayPurchaseSchema.extend({
  user: PlatformPersonalUserRefSchema,
  /** one per Play orderId (subscription renewals add rows) */
  orders: z.array(
    z.object({
      id: z.uuid(),
      status: PersonalOrderStatus,
      orderId: z.string().nullable(),
      grantId: z.uuid().nullable(),
      currency: z.string(),
      amount: Money,
      createdAt: z.iso.datetime(),
    }),
  ),
});
export type PlatformPersonalPlayPurchase = z.infer<typeof PlatformPersonalPlayPurchaseSchema>;
export const PlatformPersonalPlayPurchaseListSchema = page(PlatformPersonalPlayPurchaseSchema);
export const PlatformPersonalPlayPurchasesQuerySchema = z.object({
  userId: z.uuid().optional(),
  state: PlayPurchaseState.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** GET / PUT / DELETE /platform/personal/free-period (DELETE = back to env PERSONAL_FREE_UNTIL) */
export const PlatformPersonalFreePeriodSchema = z.object({
  /** CONSOLE = set here (overrides env), ENV = PERSONAL_FREE_UNTIL, NONE = no free period */
  source: z.enum(['CONSOLE', 'ENV', 'NONE']),
  until: z.iso.date().nullable(),
  maxPlayers: z.number(),
  /** today is on or before `until` */
  active: z.boolean(),
  envUntil: z.iso.date().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type PlatformPersonalFreePeriod = z.infer<typeof PlatformPersonalFreePeriodSchema>;
export const PlatformPersonalFreePeriodRequestSchema = z.object({
  /** null = no free period (even when env sets one) */
  until: z.iso.date().nullable(),
  /** null / omitted = env PERSONAL_FREE_MAX_PLAYERS */
  maxPlayers: z.int().min(1).max(1000).nullable().optional(),
  reason: z.string().trim().min(1).max(500),
});
export type PlatformPersonalFreePeriodRequest = z.infer<
  typeof PlatformPersonalFreePeriodRequestSchema
>;
